import {
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { addDays, differenceInDays, format as formatDate, isValid, parseISO } from 'date-fns';
import { toPng } from 'html-to-image';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DenseMenu } from '@/components/common/DenseMenu';
import { CloseButton } from '@/components/ds';
import { RenderMode, RfqDocumentEdits, RfqPage, THEMES } from '@/components/home/rfqs/RfqDocument';
import { RfqVendorOutreachPanel } from '@/components/home/rfqs/RfqVendorOutreachPanel';
import { useAuthContext } from '@/context/AuthProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { usePropertyUsers } from '@/hooks/properties/usePropertyUsers';
import { useLogger } from '@/hooks/useLogger';
import { PropertyUser, UserProperty } from '@/models/Property';
import { RequestForQuotation } from '@/models/RequestForQuotation';
import {
  createRFQShareToken,
  updateRFQContact,
  updateRFQSubmissionRequirements,
} from '@/services/api/rfqs';
import { useTranslationPrefix } from '@/translations/i18n';

export interface ReviewAndShareRFQDialogProps {
  open: boolean;
  userProperty: UserProperty;
  requestForQuotation: RequestForQuotation;
  suggestedTrades?: string[];
  onClose: () => void;
}

export const ReviewAndShareRFQDialog = ({
  open,
  userProperty,
  requestForQuotation,
  suggestedTrades,
  onClose,
}: ReviewAndShareRFQDialogProps) => {
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const logger = useLogger('ReviewAndShareRFQDialog');
  const { user } = useAuthContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [isCopying, setIsCopying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [renderMode, setRenderMode] = useState<RenderMode>('dark');
  const printableRef = useRef<HTMLDivElement>(null);
  const isBusy = isCopying || isDownloading;

  const { propertyUsers } = usePropertyUsers(requestForQuotation.propertyId);

  const initialSelectedUser = useMemo(() => {
    if (!propertyUsers.length) return null;
    const match = propertyUsers.find(
      (u) => u.userEmail === requestForQuotation.clientContact.email,
    );
    return match ?? propertyUsers[0];
  }, [propertyUsers, requestForQuotation.clientContact.email]);

  const [selectedUser, setSelectedUser] = useState<PropertyUser | null>(initialSelectedUser);
  const [issuerMenuAnchor, setIssuerMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!selectedUser && initialSelectedUser) {
      setSelectedUser(initialSelectedUser);
    }
  }, [initialSelectedUser, selectedUser]);

  const handleIssuerSelect = async (u: PropertyUser) => {
    setIssuerMenuAnchor(null);
    if (u.userEmail === selectedUser?.userEmail) return;
    setSelectedUser(u);
    try {
      await updateRFQContact({
        rfqId: requestForQuotation.id,
        data: {
          name: u.userName,
          email: u.userEmail,
          phoneNumber: u.userPhoneNumber ? String(u.userPhoneNumber) : '',
        },
      });
    } catch (err) {
      logger.error('Failed to update RFQ contact', { error: err });
      showAlert(t('rfq.contact_update_failed'), 'error');
    }
  };

  const initialDeadline = useMemo(
    () =>
      addDays(
        requestForQuotation.createdAt,
        requestForQuotation.submissionRequirements.dueInDays ?? 0,
      ),
    [requestForQuotation.createdAt, requestForQuotation.submissionRequirements.dueInDays],
  );

  const initialDesiredStart = useMemo(() => {
    const raw = requestForQuotation.timeline.desiredStartDate;
    if (!raw) return null;
    const parsedIso = parseISO(raw);
    if (isValid(parsedIso)) return parsedIso;
    const parsed = new Date(raw);
    return isValid(parsed) ? parsed : null;
  }, [requestForQuotation.timeline.desiredStartDate]);

  const [desiredStartDate, setDesiredStartDate] = useState<Date | null>(initialDesiredStart);
  const [deadlineDate, setDeadlineDate] = useState<Date | null>(initialDeadline);
  const hasRequiredFields = Boolean(desiredStartDate && deadlineDate);

  const edits: RfqDocumentEdits = {
    desiredStartDate,
    deadlineDate,
    onDesiredStartChange: setDesiredStartDate,
    onDeadlineChange: setDeadlineDate,
  };

  const persistEdits = async () => {
    const dueInDays = deadlineDate
      ? Math.max(0, differenceInDays(deadlineDate, new Date(requestForQuotation.createdAt)))
      : undefined;
    const contactEmail = selectedUser?.userEmail ?? user?.email;
    const method = contactEmail ? `Email proposal to ${contactEmail}` : undefined;
    const desiredStartStr = desiredStartDate
      ? formatDate(desiredStartDate, 'MMMM d, yyyy')
      : undefined;
    await updateRFQSubmissionRequirements({
      rfqId: requestForQuotation.id,
      data: {
        ...(dueInDays !== undefined ? { dueInDays } : {}),
        ...(method ? { method } : {}),
        ...(desiredStartStr ? { desiredStartDate: desiredStartStr } : {}),
      },
    });
  };

  const handleCopyLink = async () => {
    if (isBusy) return;
    if (!hasRequiredFields) {
      showAlert(t('rfq.dates_required'), 'error');
      return;
    }
    setIsCopying(true);
    try {
      await persistEdits();
      const token = await createRFQShareToken({ rfqIds: [requestForQuotation.id] });
      const url = `${window.location.origin}/rfq/share/${token.token}`;
      try {
        await navigator.clipboard.writeText(url);
        showAlert(t('rfq.share_link_copied'), 'success');
      } catch {
        showAlert(t('rfq.share_link_opened'), 'info');
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      logger.error('Failed to create RFQ share token', { error: err });
      showAlert(t('rfq.share_failed'), 'error');
    } finally {
      setIsCopying(false);
    }
  };

  const handleDownload = async () => {
    if (isBusy || !printableRef.current) return;
    if (!hasRequiredFields) {
      showAlert(t('rfq.dates_required'), 'error');
      return;
    }
    setIsDownloading(true);
    try {
      await persistEdits();
      setRenderMode('light');
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      const dataUrl = await toPng(printableRef.current, {
        pixelRatio: 2,
        backgroundColor: '#FFFFFF',
        cacheBust: true,
      });

      const safeAddress = (userProperty.property.address ?? 'document').replace(
        /[^a-zA-Z0-9 -]/g,
        '',
      );
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `RFQ - ${safeAddress}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      logger.error('Failed to download RFQ PNG', { error: err });
      showAlert(t('rfq.download_failed'), 'error');
    } finally {
      setRenderMode('dark');
      setIsDownloading(false);
    }
  };

  const property = userProperty.property;
  const issuer = selectedUser
    ? {
        name: selectedUser.userName,
        email: selectedUser.userEmail,
        phoneNumber: selectedUser.userPhoneNumber
          ? String(selectedUser.userPhoneNumber)
          : undefined,
      }
    : user
    ? { name: user.name, email: user.email }
    : null;

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            // the app's dialog material: the solid dialog surface with its sheen and drop, no stroke
            backgroundColor: 'var(--tx-dialog)',
            backgroundImage: 'var(--tx-surface-light)',
            border: 'none',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: isMobile ? '16px' : '24px',
            width: '100%',
            maxWidth: isMobile ? 'calc(100vw - 20px)' : '1540px',
            m: isMobile ? '10px' : { sm: '10px', md: 3 },
            // A floating card with a small inset margin, not an edge-to-edge
            // full-screen sheet — dvh (not the removed fullScreen prop's
            // height:100%) caps it to the visible viewport with the same
            // margin subtracted, so it never overflows; DialogContent scrolls
            // internally within that fixed card height.
            height: isMobile ? 'calc(100dvh - 20px)' : undefined,
            maxHeight: isMobile ? 'calc(100dvh - 20px)' : undefined,
          },
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: '20px',
          py: '16px',
          minHeight: '62px',
          borderBottom: '1px solid var(--tx-line)',
        }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-muted)', flexShrink: 0 }}>
          Verify dates before sharing
        </Typography>
        <CloseButton
          onClose={() => {
            if (!isBusy) onClose();
          }}
          label="Close"
          sx={{ position: 'absolute', top: 18, right: 18 }}
        />
      </Box>
      <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
        <Box ref={printableRef}>
          <RfqPage
            rfq={requestForQuotation}
            property={property}
            issuer={issuer}
            theme={THEMES[renderMode]}
            mode={renderMode}
            edits={edits}
            readOnly={isDownloading}
            issuerSelectable={!isDownloading && propertyUsers.length > 1}
            onIssuerClick={(e) => setIssuerMenuAnchor(e.currentTarget)}
          />
        </Box>
      </DialogContent>
      <DenseMenu
        anchorEl={issuerMenuAnchor}
        open={Boolean(issuerMenuAnchor)}
        onClose={() => setIssuerMenuAnchor(null)}>
        {propertyUsers.map((u) => (
          <MenuItem key={u.userEmail} onClick={() => handleIssuerSelect(u)}>
            {u.userName}
          </MenuItem>
        ))}
      </DenseMenu>
      <DialogActions
        sx={{
          px: '20px',
          py: '16px',
          borderTop: '1px solid var(--tx-line)',
          gap: 1.5,
          justifyContent: 'flex-end',
        }}>
        {!isMobile && (
          <Button
            onClick={onClose}
            disabled={isBusy}
            variant="text"
            sx={{ color: 'var(--tx-text)', fontSize: 14, fontWeight: 500 }}>
            Cancel
          </Button>
        )}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <RfqVendorOutreachPanel
            userProperty={userProperty}
            rfqs={[requestForQuotation]}
            issuer={issuer}
            suggestedTrades={suggestedTrades}
            disabled={isBusy}
            hasRequiredFields={hasRequiredFields}
            onBeforeOutreach={persistEdits}
            placement="header"
          />
          {isMobile ? (
            <IconButton
              onClick={handleCopyLink}
              disabled={isBusy || !hasRequiredFields}
              aria-label="Copy link"
              title="Copy link"
              sx={{
                // tonal (the ds IconButton): a fill, no stroke
                border: 'none',
                borderRadius: '10px',
                backgroundColor: 'var(--tx-fill)',
                color: 'var(--tx-text)',
                width: 40,
                height: 40,
                '&:hover': { backgroundColor: 'var(--tx-fill-2)' },
                '&.Mui-disabled': { color: 'var(--tx-faint)', backgroundColor: 'var(--tx-fill)', opacity: 0.6 },
              }}>
              {isCopying ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: '16px !important' }} />
              )}
            </IconButton>
          ) : (
            <Button
              onClick={handleCopyLink}
              disabled={isBusy || !hasRequiredFields}
              variant="outlined"
              startIcon={
                isCopying ? (
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: '16px !important' }} />
                )
              }
              sx={{
                // tonal (the ds secondary button): a fill, no stroke
                height: 40,
                px: '16px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: 'var(--tx-fill)',
                color: 'var(--tx-text)',
                fontSize: 14,
                fontWeight: 600,
                '&:hover': { border: 'none', backgroundColor: 'var(--tx-fill-2)' },
                '&.Mui-disabled': { border: 'none', color: 'var(--tx-faint)', backgroundColor: 'var(--tx-fill)', opacity: 0.6 },
              }}>
              {isCopying ? 'Generating link…' : 'Copy link'}
            </Button>
          )}
          {isMobile ? (
            <IconButton
              onClick={handleDownload}
              disabled={isBusy || !hasRequiredFields}
              aria-label="Download"
              title="Download"
              sx={{
                // tonal (the ds IconButton): a fill, no stroke
                border: 'none',
                borderRadius: '10px',
                backgroundColor: 'var(--tx-fill)',
                color: 'var(--tx-text)',
                width: 40,
                height: 40,
                '&:hover': { backgroundColor: 'var(--tx-fill-2)' },
                '&.Mui-disabled': { color: 'var(--tx-faint)', backgroundColor: 'var(--tx-fill)', opacity: 0.6 },
              }}>
              {isDownloading ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <DownloadIcon sx={{ fontSize: '16px !important' }} />
              )}
            </IconButton>
          ) : (
            <Button
              onClick={handleDownload}
              disabled={isBusy || !hasRequiredFields}
              variant="outlined"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                ) : (
                  <DownloadIcon sx={{ fontSize: '16px !important' }} />
                )
              }
              sx={{
                // tonal (the ds secondary button): a fill, no stroke
                height: 40,
                px: '16px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: 'var(--tx-fill)',
                color: 'var(--tx-text)',
                fontSize: 14,
                fontWeight: 600,
                '&:hover': { border: 'none', backgroundColor: 'var(--tx-fill-2)' },
                '&.Mui-disabled': { border: 'none', color: 'var(--tx-faint)', backgroundColor: 'var(--tx-fill)', opacity: 0.6 },
              }}>
              {isDownloading ? 'Preparing…' : 'Download'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
